import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, SupplierContract, Unternehmen } from "@kundeninfo/api-client";
import { useSofortPopup } from "@kundeninfo/ui";
import { Hinweis } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import type { DocumentFilesClient } from "../../components/Vorgangsdateien.js";
import { useJumpFocus } from "../../components/jumpFocus.js";
import { useStabileListe } from "../../components/stabileListe.js";
import { jumpNote } from "../customerOverview/targets.js";
import type { AkteSprung } from "../KundenAkte.js";
import type { ChargeListClient } from "./ChargeList.js";
import type { TermPanelClient } from "./KonditionPanel.js";
import { VertragPanel } from "./VertragPanel.js";
import type { ContractPanelClient } from "./VertragPanel.js";
import { sharedAssignmentLists } from "../../components/KontierungFelder.js";
import { LiefervertragObjekt, useContractActions } from "./LiefervertragObjekt.js";
import type { TicketWeg } from "./LiefervertragObjekt.js";
import { useTabList } from "./shared.js";
import type { SichtWahl } from "./shared.js";
import { SUPPLIER_TEXT_WEB as T } from "./texts.js";
import "./Suppliers.css";

/**
 * Der Reiter „LIEFERVERTRÄGE“ der Akte eines Lieferanten (Spec „Akte eines Lieferanten“; seit „Öffnen statt Springen“
 * Aufgabe 3 als Liste von `LiefervertragObjekt`, K12): in der Ordnung des Servers (gekündigte mit nahender Frist und
 * Posten ohne Rechnung zuerst), alle klappbar, in EINEM Behälter (W-4). Aufgeklappt Kopfdaten, Konditionen, Fälligkeiten,
 * Dateien — erst dann geladen. Die Aktionen stehen im Fuß (Panelknopf Bearbeiten, „⋯“), ohne Sprung (eigene Akte, P9);
 * oben „Neuer Liefervertrag“, gesperrt ohne Lieferantenrolle im aktiven Unternehmen (A-W6).
 *
 * Nach jedem Schreibweg — auch einem gescheiterten — lädt die Liste neu und hält das betroffene Objekt an seiner Stelle
 * (`keepInView`); die Fälligkeiten des aufgeklappten lesen dann frisch. Ein Ladefehler steht als Hinweis da. Ein Sprung
 * (`oeffneId`) klappt den Vertrag auf, rollt ihn in die Mitte, fokussiert den Klapp-Pfeil und nennt seine Quelle
 * (`useJumpFocus`, `jumpNote`); eine überholte Ladung wird verworfen (`useTabList`).
 */

export type SupplierContractsClient = ContractPanelClient & TermPanelClient & ChargeListClient & DocumentFilesClient
  & Pick<ApiClient, "getSupplierContracts" | "getSupplierContract" | "terminateSupplierContract" | "deleteContractTerm">;

export function KundeLiefervertraege(p: {
  customerId: string; sicht: SichtWahl; client: SupplierContractsClient; aktivesUnternehmen: string;
  onFehler: (e: ApiError) => void; unternehmen?: readonly Unternehmen[]; onOrdnerOeffnen?: (folderId: string) => void;
  oeffneId?: string | null; jumpSource?: AkteSprung["source"];
  /** Der Ticketweg der Akte: ohne ihn kein „Ticket anlegen“ im „⋯“. */
  ticket?: TicketWeg;
  /** Das Kürzel des aktiven Unternehmens, wenn die Firma dort keine Lieferantenrolle hat; sonst `null`/weggelassen. */
  missingRoleAt?: string | null;
}): ReactElement {
  const { client, customerId } = p;
  const sicht = useStabileListe(p.sicht);
  const [open, setOpen] = useState<string | null>(null);
  const [jumped, setJumped] = useState<string | null>(null);
  const [neu, setNeu] = useState(false);
  const [lists] = useState(() => sharedAssignmentLists(client));
  const key = `${customerId}|${JSON.stringify(sicht ?? null)}`;
  const list = useTabList<SupplierContract>("data-contract-id", key,
    () => client.getSupplierContracts(customerId, sicht === undefined ? {} : { companyIds: sicht }),
    (e) => { if (e instanceof ApiError) p.onFehler(e); });
  const { items, load } = list;
  const companies = p.unternehmen ?? [];
  const aktionen = useContractActions({ client, lists, companies, ...(p.ticket === undefined ? {} : { ticket: p.ticket }),
    onDone: (id) => { void load(id); } });
  // Ein Wechsel der Sicht klappt zu: Der aufgeklappte Vertrag steht in der neuen nicht unbedingt.
  useEffect(() => { setOpen(null); }, [key]);
  // Der Fokus des Sprungs landet am Klapp-Pfeil: `me-objekt.focus()` leitet dorthin (W-8).
  useJumpFocus("data-contract-id", p.oeffneId, items,
    { waitForEntry: true, open: (id) => { setOpen(id); setJumped(id); } });
  const missingRole = p.missingRoleAt ?? null;
  const roleTip = useSofortPopup<HTMLSpanElement>(missingRole === null ? "" : T.noSupplierRole(missingRole));
  const gesperrt = neu || aktionen.offen;

  return (
    <div ref={list.root} className="sup">
      <div className="sup__toolbar">
        <span ref={roleTip}>
          <KiKnopf art="primaer" deaktiviert={gesperrt || missingRole !== null}
            onClick={() => setNeu(true)}>{T.newContract}</KiKnopf>
        </span>
        {missingRole !== null && <p className="sup__muted">{T.noSupplierRole(missingRole)}</p>}
      </div>
      {list.failed && <Hinweis art="fehler">{T.loadFailed}</Hinweis>}
      {items === null && !list.failed && <p className="sup__muted" role="status">{T.loading}</p>}
      {items !== null && items.length === 0 && !list.failed && <p className="sup__muted">{T.empty}</p>}
      {items !== null && items.length > 0 && (
        // W-4: alle Objekte direkte Geschwister EINES Behälters — me-objekt gleicht die Spalten darüber an.
        <div className="sup__objekte">
          {items.map((c) => (
            <LiefervertragObjekt key={c.id} vertrag={c} ort="akte" aufgeklappt={open === c.id}
              onAufgeklappt={(offen) => { setOpen(offen ? c.id : null); setJumped(null); }}
              client={client} lists={lists} companies={companies} mehrereUnternehmen={sicht !== undefined && sicht.length > 1}
              refresh={list.stamp} mitTicket={aktionen.mitTicket} gesperrt={gesperrt}
              onAktion={(a) => aktionen.start(a, c)} onGeaendert={() => { void load(c.id); }}
              {...(p.onOrdnerOeffnen === undefined ? {} : { onOrdnerOeffnen: p.onOrdnerOeffnen })}
              jumpNote={jumped === c.id ? jumpNote(p.jumpSource, T.jumpFromArea) : null} />
          ))}
        </div>
      )}

      {neu && (
        <VertragPanel customerId={customerId} companyId={p.aktivesUnternehmen} client={client} lists={lists} verlauf
          onGespeichert={(c) => { setNeu(false); setOpen(c.id); void load(); }} onSchliessen={() => setNeu(false)} />
      )}
      {aktionen.panels}
    </div>
  );
}
